import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Trash2, AlertCircle, ChevronDown, CalendarClock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Field, Input, Select, Textarea, Button, Spinner, Pill,
} from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import CrewRosterPanel from "@/features/crews/CrewRosterPanel";

// =============================================================================
// ProjectCrewAssignmentsCard — embed on ProjectDetailScreen.
// Lists current + past crew assignments for the project, lets manager+ add or
// soft-end them. Reads from project_crew_assignments + crews.
// =============================================================================

export interface Props {
  projectId: string;
  projectScheduledStart: string | null;
  projectScheduledEnd: string | null;
}

interface AssignmentRow {
  pca_id:        string;
  crew_id:       string;
  assigned_from: string;
  assigned_to:   string | null;
  notes:         string | null;
  crews: {
    crew_id:   string;
    crew_code: string;
    crew_name: string;
    colour_hex:string;
  } | null;
}

interface CrewOption {
  crew_id:    string;
  crew_code:  string;
  crew_name:  string;
  colour_hex: string;
  active:     boolean;
}

// -----------------------------------------------------------------------------

export default function ProjectCrewAssignmentsCard({ projectId, projectScheduledStart, projectScheduledEnd }: Props) {
  const qc = useQueryClient();
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const [adding, setAdding] = useState(false);
  // Track which assignment rows have their roster expanded (per pca_id).
  const [expandedPcas, setExpandedPcas] = useState<Set<string>>(new Set());
  function togglePca(id: string) {
    setExpandedPcas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const { data: assignments, isLoading, refetch } = useQuery({
    queryKey: ["project_crew_assignments", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_crew_assignments")
        .select("pca_id, crew_id, assigned_from, assigned_to, notes, crews:crew_id (crew_id, crew_code, crew_name, colour_hex)")
        .eq("project_id", projectId)
        .order("assigned_from", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AssignmentRow[];
    },
  });

  async function removeAssignment(pcaId: string) {
    if (!window.confirm("Remove this crew assignment from the project?")) return;
    const { error } = await supabase
      .from("project_crew_assignments")
      .delete()
      .eq("pca_id", pcaId);
    if (error) { window.alert(`Couldn't remove: ${error.message}`); return; }
    refetch();
    qc.invalidateQueries({ queryKey: ["v_project_crew_calendar"] });
    qc.invalidateQueries({ queryKey: ["schedule_pcas"] });
  }

  // Inline edit of a crew's own on-site window (to adjust as a job moves).
  const [editingPca, setEditingPca] = useState<string | null>(null);
  const [editFrom, setEditFrom] = useState("");
  const [editTo, setEditTo] = useState("");
  function startEdit(a: AssignmentRow) { setEditingPca(a.pca_id); setEditFrom(a.assigned_from); setEditTo(a.assigned_to ?? ""); }
  async function saveDates(pcaId: string) {
    if (!editFrom || (editTo && editTo < editFrom)) return;
    const { error } = await supabase
      .from("project_crew_assignments")
      .update({ assigned_from: editFrom, assigned_to: editTo || null })
      .eq("pca_id", pcaId);
    // 8 Oct 2026 (mig 425) — this used to swallow the error: a refused save
    // (a stand-down or carrier embargo on the new days) just looked like
    // nothing happened. Say why.
    if (error) { window.alert(`Couldn't save those dates: ${error.message}`); return; }
    setEditingPca(null);
    refetch();
    qc.invalidateQueries({ queryKey: ["v_project_crew_calendar"] });
    qc.invalidateQueries({ queryKey: ["schedule_pcas"] });
    qc.invalidateQueries({ queryKey: ["schedule_roster"] });
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-text-muted"/>
            <span className="text-[12px] uppercase tracking-wider text-text-muted">Crews on site</span>
          </div>
          {canManage && !adding && (
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>
              Assign crew
            </Button>
          )}
        </div>

        {isLoading && <div className="flex justify-center py-3"><Spinner size={14}/></div>}

        {!isLoading && (assignments ?? []).length === 0 && !adding && (
          <div className="text-[12px] text-text-muted py-2">
            No crews assigned yet. {canManage ? "Use Assign crew to add one." : ""}
          </div>
        )}

        {!isLoading && (assignments ?? []).length > 0 && (
          <div className="space-y-1.5">
            {assignments!.map(a => {
              const _today = new Date().toISOString().slice(0, 10);
              const _ended = a.assigned_to && a.assigned_to < _today;
              const _upcoming = a.assigned_from > _today;
              const expanded = expandedPcas.has(a.pca_id);
              return (
                <div key={a.pca_id} className="bg-ink rounded border border-line/30 overflow-hidden">
                  <div className="flex items-center justify-between gap-2 pr-2">
                    <button
                      type="button"
                      onClick={() => a.crew_id && togglePca(a.pca_id)}
                      className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 hover:bg-ink-hover text-left"
                      title={expanded ? "Hide crew members" : "Show crew members"}
                    >
                      <ChevronDown
                        size={12}
                        className={`text-text-muted flex-shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
                      />
                      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: a.crews?.colour_hex ?? "#666" }}/>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium truncate">
                          <span className="font-mono">{a.crews?.crew_code}</span>
                          <span className="text-text-muted ml-2">{a.crews?.crew_name}</span>
                        </div>
                        <div className="text-[10px] text-text-muted">
                          {fmtDate(a.assigned_from)} → {a.assigned_to ? fmtDate(a.assigned_to) : "project end"}
                          {a.notes && <span className="ml-2 italic">· {a.notes}</span>}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {_ended
                        ? <Pill label="Past"     tone="closed" small/>
                        : _upcoming
                          ? <Pill label="Upcoming" tone="pending" small/>
                          : <Pill label="Current"  tone="ok" small/>}
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => (editingPca === a.pca_id ? setEditingPca(null) : startEdit(a))}
                          className="text-[10px] text-text-muted hover:text-accent inline-flex items-center gap-1"
                          title="Edit this crew's on-site window"
                        >
                          <CalendarClock size={10}/> dates
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => removeAssignment(a.pca_id)}
                          className="text-[10px] text-text-muted hover:text-lost inline-flex items-center gap-1"
                          title="Remove this crew assignment"
                        >
                          <Trash2 size={10}/> remove
                        </button>
                      )}
                    </div>
                  </div>
                  {editingPca === a.pca_id && canManage && (
                    <div className="border-t border-line/40 bg-ink-raised/40 px-3 py-2 flex items-end gap-2 flex-wrap">
                      <label className="text-[10px] text-text-muted">On site from
                        <input type="date" value={editFrom} onChange={e => setEditFrom(e.target.value)}
                          className="mt-0.5 block bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
                      </label>
                      <label className="text-[10px] text-text-muted">Until
                        <input type="date" value={editTo} min={editFrom || undefined} onChange={e => setEditTo(e.target.value)}
                          className="mt-0.5 block bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
                      </label>
                      <Button size="sm" onClick={() => saveDates(a.pca_id)} disabled={!editFrom}>Save</Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditingPca(null)}>Cancel</Button>
                      <span className="text-[10px] text-text-dim italic self-center">Blank “Until” = open-ended</span>
                    </div>
                  )}
                  {expanded && a.crew_id && (
                    <div className="border-t border-line/40 bg-ink-raised/40 px-2 py-2">
                      <CrewRosterPanel crewId={a.crew_id}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {adding && canManage && (
          <AddAssignmentForm
            projectId={projectId}
            projectScheduledStart={projectScheduledStart}
            projectScheduledEnd={projectScheduledEnd}
            onClose={() => { setAdding(false); refetch(); qc.invalidateQueries({ queryKey: ["v_project_crew_calendar"] }); }}
          />
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------

function AddAssignmentForm({
  projectId, projectScheduledStart, projectScheduledEnd, onClose,
}: {
  projectId: string;
  projectScheduledStart: string | null;
  projectScheduledEnd: string | null;
  onClose: () => void;
}) {
  // Phased crews (James 2026-07-02): a project isn't one crew for its whole
  // window — e.g. Civil first, then Rigging starting +7 days after cure, and a
  // long job may run a second rigging team later. So each assignment carries its
  // OWN start/end window. Defaults to the project window; edit to stagger.
  const today = new Date().toISOString().slice(0, 10);
  const [crewId, setCrewId]   = useState("");
  const [fromDate, setFromDate] = useState(projectScheduledStart ?? today);
  const [toDate,   setToDate]   = useState(projectScheduledEnd ?? "");
  const [notes,  setNotes]    = useState("");
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState<string | null>(null);

  const { data: crewOptions } = useQuery({
    queryKey: ["crews_pickable"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crews")
        .select("crew_id, crew_code, crew_name, colour_hex, active")
        .eq("active", true)
        .order("crew_code");
      if (error) throw error;
      return (data ?? []) as CrewOption[];
    },
  });

  async function save() {
    if (!crewId || !fromDate) return;
    if (toDate && toDate < fromDate) { setError("End date can't be before the start date."); return; }
    setSaving(true); setError(null);
    try {
      const { error } = await supabase.from("project_crew_assignments").insert({
        project_id:    projectId,
        crew_id:       crewId,
        assigned_from: fromDate,
        assigned_to:   toDate || null,   // blank = open-ended
        notes:         notes.trim() || null,
      });
      if (error) throw error;
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 border-t border-line pt-3 space-y-2">
      <Field label="Crew">
        <Select value={crewId} onChange={e => setCrewId(e.target.value)}>
          <option value="">— pick a crew —</option>
          {(crewOptions ?? []).map(c => (
            <option key={c.crew_id} value={c.crew_id}>{c.crew_code} · {c.crew_name}</option>
          ))}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="On site from">
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </Field>
        <Field label="Until" hint="Blank = open-ended">
          <Input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes" hint="Optional — context for the team">
        <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"/>
      </Field>
      <div className="text-[10px] text-text-muted italic">
        Defaults to the project window — change the dates to stagger crews (e.g. Civil first, Rigging starting later).
      </div>
      {error && (
        <div className="text-[12px] text-lost inline-flex items-center gap-1">
          <AlertCircle size={11}/> {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!crewId || saving} onClick={save}>
          {saving ? <Spinner size={12}/> : "Add assignment"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}
